import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format, subDays, subMonths } from 'date-fns';
import { useApp } from '../context/AppContext';

export const Reports = () => {
  const { data } = useApp();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Comprehensive report library
  const reportLibrary = [
    {
      id: 'RPT-2025-001',
      category: 'Audit Pack',
      title: 'Project Audit Pack - Windhoek-Okahandja Dual Carriageway',
      description: 'Complete audit documentation including contracts, payments, milestones, and compliance records',
      projectId: 'NAM-2025-0012',
      ministry: 'Ministry of Works & Transport',
      generatedDate: '2025-01-20T14:30:00Z',
      generatedBy: 'Prime Minister\'s Office - Audit Unit',
      fileSize: '45.2 MB',
      pages: 342,
      status: 'Published',
      confidentiality: 'Confidential',
      sections: [
        'Executive Summary',
        'Project Overview & Justification',
        'Procurement Process Documentation',
        'Contract Agreement & Amendments',
        'Payment Certificates & Invoices',
        'Milestone Evidence & Photos',
        'Compliance Deadlines Log',
        'Risk Register & Mitigation',
        'Quality Assurance Reports',
        'Auditor General Queries & Responses'
      ],
      attachments: 87,
      legalBasis: 'Public Procurement Act S.7(4), Audit Act (2005)',
      recipients: ['Office of the Auditor General', 'Public Procurement Unit', 'Cabinet'],
      auditFindings: 3,
      recommendations: 5,
      tags: ['Infrastructure', 'Roads', 'NDP6 Pillar 1']
    },
    {
      id: 'RPT-2025-002',
      category: 'NDP6 Report',
      title: 'NDP6 Q1 FY 2024/25 Consolidated Progress Report',
      description: 'Quarterly progress report covering all 4 NDP6 pillars with ministry-level performance data',
      projectId: null,
      ministry: 'National Planning Commission',
      generatedDate: '2024-10-15T09:00:00Z',
      generatedBy: 'National Planning Commission',
      fileSize: '28.7 MB',
      pages: 156,
      status: 'Published',
      confidentiality: 'Public',
      sections: [
        'National Overview & Key Highlights',
        'Pillar 1: Economic Advancement',
        'Pillar 2: Social Transformation',
        'Pillar 3: Environmental Sustainability',
        'Pillar 4: Good Governance',
        'Ministry Performance Rankings',
        'Budget Execution Analysis',
        'Critical Gaps & Recommendations',
        'Quarterly KPI Dashboard',
        'Annexes: Raw Data & Methodology'
      ],
      attachments: 45,
      legalBasis: 'NDP6 IMEP Framework',
      recipients: ['Cabinet', 'Parliament', 'National Planning Commission'],
      auditFindings: 0,
      recommendations: 12,
      tags: ['NDP6', 'Quarterly', 'All Pillars']
    },
    {
      id: 'RPT-2025-003',
      category: 'Compliance Report',
      title: 'Public Procurement Compliance Report - December 2024',
      description: 'Monthly compliance monitoring report covering all OMAs procurement activities',
      projectId: null,
      ministry: 'Public Procurement Unit',
      generatedDate: '2025-01-10T11:00:00Z',
      generatedBy: 'Public Procurement Unit',
      fileSize: '12.4 MB',
      pages: 89,
      status: 'Published',
      confidentiality: 'Restricted',
      sections: [
        'Executive Summary',
        'Annual Procurement Plans Status',
        'Award Publication Compliance (7-day rule)',
        'Funds Certification Compliance',
        'Non-Compliance Notices Issued',
        'Ministry-by-Ministry Scorecard',
        'Contractor Performance Overview',
        'Debarment Actions Taken',
        'Recommendations for Improvement',
        'Annexes: Supporting Documentation'
      ],
      attachments: 34,
      legalBasis: 'Public Procurement Act (2015), PP Regulations (2017)',
      recipients: ['Prime Minister\'s Office', 'Ministry of Finance', 'Cabinet'],
      auditFindings: 8,
      recommendations: 15,
      tags: ['Procurement', 'Compliance', 'Monthly']
    },
    {
      id: 'RPT-2025-004',
      category: 'Financial Report',
      title: 'Budget Execution Report - Mid-Year FY 2024/25',
      description: 'Comprehensive budget performance analysis across all votes and ministries',
      projectId: null,
      ministry: 'Ministry of Finance',
      generatedDate: '2024-12-20T16:00:00Z',
      generatedBy: 'Ministry of Finance - Budget Monitoring Unit',
      fileSize: '35.8 MB',
      pages: 203,
      status: 'Published',
      confidentiality: 'Confidential',
      sections: [
        'National Budget Overview',
        'Revenue Collection Performance',
        'Expenditure Analysis by Vote',
        'Budget Execution Rates by Ministry',
        'Virements & Reallocations',
        'Underspending & Overspending Analysis',
        'Capital vs Operational Expenditure',
        'Debt Servicing & Commitments',
        'Fiscal Risk Assessment',
        'Recommendations & Budget Acceleration Plans'
      ],
      attachments: 67,
      legalBasis: 'State Finance Act (1991), Treasury Instructions',
      recipients: ['Cabinet', 'Parliament', 'All OMAs'],
      auditFindings: 5,
      recommendations: 18,
      tags: ['Budget', 'Financial', 'Mid-Year']
    },
    {
      id: 'RPT-2024-005',
      category: 'Audit Pack',
      title: 'Project Audit Pack - Khomas District Hospital Phase 1',
      description: 'Complete audit trail for hospital construction project from inception to current stage',
      projectId: 'NAM-2025-0045',
      ministry: 'Ministry of Health & Social Services',
      generatedDate: '2024-11-25T10:15:00Z',
      generatedBy: 'Prime Minister\'s Office - Audit Unit',
      fileSize: '52.1 MB',
      pages: 418,
      status: 'Published',
      confidentiality: 'Confidential',
      sections: [
        'Executive Summary',
        'Project Feasibility Study',
        'Procurement Process & Tender Evaluation',
        'Contract Award Documentation',
        'Payment Schedules & Certificates',
        'Milestone Progress & Evidence',
        'Quality Control Reports',
        'Environmental Compliance',
        'Stakeholder Consultations',
        'Audit Findings & Management Responses'
      ],
      attachments: 124,
      legalBasis: 'Public Procurement Act S.7(4), Health Act',
      recipients: ['Office of the Auditor General', 'Ministry of Health'],
      auditFindings: 2,
      recommendations: 7,
      tags: ['Health', 'Infrastructure', 'NDP6 Pillar 2']
    },
    {
      id: 'RPT-2024-006',
      category: 'Performance Report',
      title: 'Ministerial Performance Report - Q4 FY 2023/24',
      description: 'Quarterly assessment of ministerial performance agreements and KPI achievement',
      projectId: null,
      ministry: 'Office of the Prime Minister',
      generatedDate: '2024-07-15T14:00:00Z',
      generatedBy: 'Office of the Prime Minister - Performance Monitoring',
      fileSize: '18.9 MB',
      pages: 127,
      status: 'Published',
      confidentiality: 'Restricted',
      sections: [
        'National Performance Overview',
        'Ministry-by-Ministry Performance Cards',
        'KPI Achievement Analysis',
        'Service Delivery Standards Compliance',
        'Public Service Charter Performance',
        'Performance Agreement Reviews',
        'Top Performers & Laggards',
        'Capacity Constraints Identified',
        'Recommendations for Improvement',
        'Annexes: Individual Ministry Reports'
      ],
      attachments: 56,
      legalBasis: 'Public Service Act (1995), Performance Management Framework',
      recipients: ['Cabinet', 'All Permanent Secretaries'],
      auditFindings: 0,
      recommendations: 23,
      tags: ['Performance', 'Quarterly', 'All Ministries']
    },
    {
      id: 'RPT-2024-007',
      category: 'Audit Response',
      title: 'Auditor General Recommendations Implementation Report',
      description: 'Status update on implementation of AG audit findings from FY 2022/23',
      projectId: null,
      ministry: 'All Ministries',
      generatedDate: '2024-09-30T11:30:00Z',
      generatedBy: 'Office of the Prime Minister',
      fileSize: '22.3 MB',
      pages: 145,
      status: 'Published',
      confidentiality: 'Public',
      sections: [
        'Executive Summary',
        'Overview of AG Findings FY 2022/23',
        'Implementation Progress by Ministry',
        'Outstanding Recommendations',
        'Financial Irregularities Addressed',
        'Systemic Issues Identified',
        'Capacity Building Initiatives',
        'Recommendations Still Pending',
        'Timeline for Full Compliance',
        'Annexes: Ministry-Specific Action Plans'
      ],
      attachments: 42,
      legalBasis: 'Audit Act (2005), State Finance Act',
      recipients: ['Office of the Auditor General', 'Parliament PAC', 'All OMAs'],
      auditFindings: 74,
      recommendations: 38,
      tags: ['Audit', 'Compliance', 'Implementation']
    },
    {
      id: 'RPT-2024-008',
      category: 'Investigation Report',
      title: 'Project Abandonment Investigation - School Renovation Kunene',
      description: 'Forensic investigation into abandoned school renovation project',
      projectId: 'NAM-2023-0099',
      ministry: 'Ministry of Education, Arts & Culture',
      generatedDate: '2024-08-12T09:00:00Z',
      generatedBy: 'Prime Minister\'s Office - Investigation Unit',
      fileSize: '31.5 MB',
      pages: 178,
      status: 'Confidential',
      confidentiality: 'Highly Confidential',
      sections: [
        'Investigation Summary',
        'Project Background & Procurement',
        'Timeline of Events',
        'Contractor Performance Analysis',
        'Payment History & Financial Audit',
        'Site Inspection Reports',
        'Stakeholder Interviews',
        'Legal & Contractual Analysis',
        'Findings & Root Causes',
        'Recommendations & Disciplinary Actions'
      ],
      attachments: 93,
      legalBasis: 'Public Procurement Act, Contract Law',
      recipients: ['Cabinet', 'Anti-Corruption Commission', 'PPU'],
      auditFindings: 12,
      recommendations: 9,
      tags: ['Investigation', 'Abandonment', 'Education']
    },
    {
      id: 'DRAFT-2025-001',
      category: 'NDP6 Report',
      title: 'NDP6 Q2 FY 2024/25 Consolidated Progress Report (DRAFT)',
      description: 'Draft quarterly report awaiting final ministry submissions',
      projectId: null,
      ministry: 'National Planning Commission',
      generatedDate: '2025-01-18T15:00:00Z',
      generatedBy: 'National Planning Commission',
      fileSize: '24.1 MB',
      pages: 142,
      status: 'Draft',
      confidentiality: 'Internal',
      sections: [
        'National Overview (INCOMPLETE)',
        'Pillar 1: Economic Advancement',
        'Pillar 2: Social Transformation',
        'Pillar 3: Environmental Sustainability',
        'Pillar 4: Good Governance (PENDING DATA)',
        'Ministry Performance Rankings (PARTIAL)',
        'Budget Execution Analysis',
        'Critical Gaps & Recommendations',
        'Quarterly KPI Dashboard',
        'Annexes: Raw Data'
      ],
      attachments: 38,
      legalBasis: 'NDP6 IMEP Framework',
      recipients: ['Internal Review Only'],
      auditFindings: 0,
      recommendations: 0,
      tags: ['NDP6', 'Quarterly', 'Draft']
    },
    {
      id: 'RPT-2024-009',
      category: 'Contractor Report',
      title: 'Contractor Performance Evaluation - China Jiangsu International',
      description: 'Annual performance review of major government contractor',
      projectId: null,
      ministry: 'Public Procurement Unit',
      generatedDate: '2024-12-05T10:00:00Z',
      generatedBy: 'Public Procurement Unit - Contractor Registry',
      fileSize: '15.7 MB',
      pages: 98,
      status: 'Published',
      confidentiality: 'Restricted',
      sections: [
        'Contractor Profile & History',
        'Active Contracts Overview',
        'Performance Scorecard',
        'Quality of Work Assessment',
        'Timeliness & Schedule Adherence',
        'Budget Compliance',
        'Health & Safety Record',
        'Labor & Local Content Compliance',
        'Complaints & Disputes Log',
        'Overall Rating & Recommendations'
      ],
      attachments: 29,
      legalBasis: 'Public Procurement Regulations 2017',
      recipients: ['All OMAs', 'PPU', 'Prime Minister\'s Office'],
      auditFindings: 1,
      recommendations: 4,
      tags: ['Contractor', 'Performance', 'Annual']
    },
    {
      id: 'RPT-2025-005',
      category: 'Parliamentary Report',
      title: 'Parliamentary Questions & Petitions Status Report - January 2025',
      description: 'Monthly report on parliamentary questions answered and petitions resolved',
      projectId: null,
      ministry: 'Office of the Prime Minister',
      generatedDate: '2025-01-21T16:30:00Z',
      generatedBy: 'Office of the Prime Minister - Parliamentary Liaison',
      fileSize: '8.4 MB',
      pages: 54,
      status: 'Published',
      confidentiality: 'Public',
      sections: [
        'Summary of Questions Received',
        'Response Rate by Ministry',
        'Overdue Responses',
        'Petitions Received & Status',
        'Petitions Resolved',
        'Recurring Themes & Issues',
        'Ministry Performance Rankings',
        'Recommendations for Improvement',
        'Annexes: Full Q&A Log'
      ],
      attachments: 18,
      legalBasis: 'Constitution of Namibia, Parliamentary Standing Orders',
      recipients: ['National Assembly', 'All OMAs'],
      auditFindings: 0,
      recommendations: 6,
      tags: ['Parliament', 'Questions', 'Monthly']
    },
    {
      id: 'RPT-2024-010',
      category: 'Risk Report',
      title: 'National Risk Register - Infrastructure Projects',
      description: 'Comprehensive risk assessment of all major infrastructure projects',
      projectId: null,
      ministry: 'Office of the Prime Minister',
      generatedDate: '2024-10-30T14:00:00Z',
      generatedBy: 'Prime Minister\'s Office - Risk Management Unit',
      fileSize: '27.9 MB',
      pages: 167,
      status: 'Published',
      confidentiality: 'Confidential',
      sections: [
        'Executive Summary',
        'Risk Assessment Methodology',
        'High-Risk Projects Identified',
        'Financial Risks',
        'Contractor Capacity Risks',
        'Environmental & Social Risks',
        'Geopolitical & Economic Risks',
        'Mitigation Strategies',
        'Monitoring & Escalation Protocols',
        'Annexes: Project-Specific Risk Profiles'
      ],
      attachments: 61,
      legalBasis: 'Project Management Framework, Risk Management Policy',
      recipients: ['Cabinet', 'All Infrastructure Ministries'],
      auditFindings: 0,
      recommendations: 28,
      tags: ['Risk', 'Infrastructure', 'Strategic']
    }
  ];

  const categories = [
    { id: 'all', label: 'All Reports', icon: FiIcons.FiGrid },
    { id: 'Audit Pack', label: 'Audit Packs', icon: FiIcons.FiShield },
    { id: 'NDP6 Report', label: 'NDP6 Reports', icon: FiIcons.FiTarget },
    { id: 'Compliance Report', label: 'Compliance', icon: FiIcons.FiCheckSquare },
    { id: 'Financial Report', label: 'Financial', icon: FiIcons.FiDollarSign },
    { id: 'Performance Report', label: 'Performance', icon: FiIcons.FiTrendingUp },
    { id: 'Investigation Report', label: 'Investigations', icon: FiIcons.FiAlertTriangle }
  ];

  const statusConfig = {
    'Published': { color: 'green', icon: FiIcons.FiCheckCircle },
    'Draft': { color: 'amber', icon: FiIcons.FiEdit },
    'Confidential': { color: 'red', icon: FiIcons.FiLock },
    'Under Review': { color: 'blue', icon: FiIcons.FiEye }
  };

  const confidentialityConfig = {
    'Public': { color: 'green', icon: FiIcons.FiGlobe },
    'Internal': { color: 'blue', icon: FiIcons.FiUsers },
    'Restricted': { color: 'amber', icon: FiIcons.FiEyeOff },
    'Confidential': { color: 'red', icon: FiIcons.FiLock },
    'Highly Confidential': { color: 'red', icon: FiIcons.FiShield }
  };

  const { filteredReports, stats } = useMemo(() => {
    let filtered = reportLibrary;

    if (activeTab !== 'all') {
      filtered = filtered.filter(r => r.category === activeTab);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.id.toLowerCase().includes(query) ||
        r.ministry.toLowerCase().includes(query) ||
        (r.projectId && r.projectId.toLowerCase().includes(query)) ||
        r.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.generatedDate) - new Date(a.generatedDate));

    const stats = {
      total: reportLibrary.length,
      published: reportLibrary.filter(r => r.status === 'Published').length,
      draft: reportLibrary.filter(r => r.status === 'Draft').length,
      confidential: reportLibrary.filter(r => r.confidentiality === 'Confidential' || r.confidentiality === 'Highly Confidential').length,
      auditPacks: reportLibrary.filter(r => r.category === 'Audit Pack').length,
      totalFindings: reportLibrary.reduce((sum, r) => sum + r.auditFindings, 0)
    };

    return { filteredReports: filtered, stats };
  }, [activeTab, searchQuery, reportLibrary]);

  const handleGenerateReport = async (reportType) => {
    setGeneratingReport(true);
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    setGeneratingReport(false);
    setShowGenerateModal(false);
    alert(`${reportType} generated successfully!`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Audit Packs</h1>
          <p className="text-slate-500">
            Comprehensive report library and audit documentation repository
          </p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiFilter} />
            Advanced Filters
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2"
          >
            <SafeIcon icon={FiIcons.FiPlus} />
            Generate Report
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="text-center p-4">
          <div className="text-3xl font-black text-slate-900">{stats.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Total Reports
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{stats.published}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Published</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-amber-500">
          <div className="text-3xl font-black text-amber-700">{stats.draft}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Draft</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-red-600">
          <div className="text-3xl font-black text-red-700">{stats.confidential}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Confidential</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-grn-navy">{stats.auditPacks}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Audit Packs</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-orange-500">
          <div className="text-3xl font-black text-orange-700">{stats.totalFindings}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Total Findings</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Filter by Category
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap',
                    activeTab === cat.id
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  <SafeIcon icon={cat.icon} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <div className="relative">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by title, ID, ministry, project, or tags..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Results Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-bold text-slate-700">
          Showing {filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''}
        </h2>
        <select className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white">
          <option>Sort by: Date (Newest First)</option>
          <option>Sort by: Date (Oldest First)</option>
          <option>Sort by: Category</option>
          <option>Sort by: Ministry</option>
          <option>Sort by: File Size</option>
        </select>
      </div>

      {/* Report Cards */}
      {filteredReports.length > 0 ? (
        <div className="space-y-4">
          {filteredReports.map(report => {
            const statusInfo = statusConfig[report.status];
            const confidentialityInfo = confidentialityConfig[report.confidentiality];

            return (
              <Card
                key={report.id}
                className="hover:shadow-lg transition-all"
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      <div className="p-4 bg-grn-navy/5 rounded-lg border border-grn-navy/10">
                        <SafeIcon icon={FiIcons.FiFileText} className="text-3xl text-grn-navy" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded uppercase tracking-wider">
                            {report.category}
                          </span>
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium flex items-center gap-1',
                            statusInfo.color === 'green' ? 'bg-green-100 text-green-700' :
                            statusInfo.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                            statusInfo.color === 'red' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          )}>
                            <SafeIcon icon={statusInfo.icon} className="text-xs" />
                            {report.status}
                          </span>
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium flex items-center gap-1',
                            confidentialityInfo.color === 'green' ? 'bg-green-100 text-green-700' :
                            confidentialityInfo.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                            confidentialityInfo.color === 'red' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          )}>
                            <SafeIcon icon={confidentialityInfo.icon} className="text-xs" />
                            {report.confidentiality}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-mono">
                            {report.id}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">
                          {report.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-3">
                          {report.description}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiBuilding} className="text-slate-400" />
                            <span className="text-slate-500 font-medium">Ministry:</span>
                            <span className="text-slate-800">{report.ministry}</span>
                          </div>
                          {report.projectId && (
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiIcons.FiBriefcase} className="text-slate-400" />
                              <span className="text-slate-500 font-medium">Project:</span>
                              <span className="font-mono text-slate-800">{report.projectId}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiCalendar} className="text-slate-400" />
                            <span className="text-slate-500 font-medium">Generated:</span>
                            <span className="text-slate-800">
                              {format(new Date(report.generatedDate), 'dd MMM yyyy, HH:mm')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiUser} className="text-slate-400" />
                            <span className="text-slate-500 font-medium">By:</span>
                            <span className="text-slate-800">{report.generatedBy}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-2xl font-black text-slate-900">{report.pages}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pages</div>
                      <div className="text-sm font-bold text-slate-600">{report.fileSize}</div>
                    </div>
                  </div>

                  {/* Metadata Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiIcons.FiPaperclip} className="text-slate-400 text-sm" />
                        <span className="text-xs font-medium text-slate-600">Attachments</span>
                      </div>
                      <div className="text-2xl font-black text-slate-900">{report.attachments}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiIcons.FiLayers} className="text-slate-400 text-sm" />
                        <span className="text-xs font-medium text-slate-600">Sections</span>
                      </div>
                      <div className="text-2xl font-black text-slate-900">{report.sections.length}</div>
                    </div>
                    {report.auditFindings > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 mb-1">
                          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600 text-sm" />
                          <span className="text-xs font-medium text-red-700">Findings</span>
                        </div>
                        <div className="text-2xl font-black text-red-700">{report.auditFindings}</div>
                      </div>
                    )}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-blue-600 text-sm" />
                        <span className="text-xs font-medium text-blue-700">Recommendations</span>
                      </div>
                      <div className="text-2xl font-black text-blue-700">{report.recommendations}</div>
                    </div>
                  </div>

                  {/* Expandable Sections */}
                  {selectedReport?.id === report.id && (
                    <div className="pt-4 border-t border-slate-100">
                      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                        Report Sections
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {report.sections.map((section, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200 text-xs"
                          >
                            <SafeIcon icon={FiIcons.FiFileText} className="text-slate-400" />
                            <span className="text-slate-700">{section}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                          Legal Basis
                        </h4>
                        <p className="text-sm text-slate-600 font-mono">{report.legalBasis}</p>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                          Recipients
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {report.recipients.map((recipient, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200"
                            >
                              {recipient}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                          Tags
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {report.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-slate-100">
                    <button
                      onClick={() => setSelectedReport(selectedReport?.id === report.id ? null : report)}
                      className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={selectedReport?.id === report.id ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} />
                      {selectedReport?.id === report.id ? 'Hide Details' : 'View Details'}
                    </button>
                    <button className="flex-1 px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors flex items-center justify-center gap-2">
                      <SafeIcon icon={FiIcons.FiDownload} />
                      Download PDF
                    </button>
                    <button className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                      <SafeIcon icon={FiIcons.FiShare2} />
                      Share Report
                    </button>
                    {report.status === 'Published' && (
                      <button className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center gap-2">
                        <SafeIcon icon={FiIcons.FiPrinter} />
                        Print
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="text-center py-12">
          <div className="flex flex-col items-center justify-center text-slate-400">
            <SafeIcon icon={FiIcons.FiInbox} className="text-4xl mb-3" />
            <h3 className="text-lg font-bold text-slate-600 mb-1">No reports found</h3>
            <p className="text-sm">Try adjusting your filters or search query</p>
          </div>
        </Card>
      )}

      {/* Generate Report Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Generate New Report</h2>
                <p className="text-sm text-slate-500">Select report type and configuration</p>
              </div>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { type: 'Project Audit Pack', icon: FiIcons.FiShield, desc: 'Complete audit trail for a specific project' },
                  { type: 'NDP6 Quarterly Report', icon: FiIcons.FiTarget, desc: 'Consolidated NDP6 progress report' },
                  { type: 'Procurement Compliance', icon: FiIcons.FiShoppingCart, desc: 'Monthly procurement compliance report' },
                  { type: 'Budget Execution', icon: FiIcons.FiDollarSign, desc: 'Budget performance analysis' },
                  { type: 'Ministry Performance', icon: FiIcons.FiTrendingUp, desc: 'Ministerial KPI performance report' },
                  { type: 'Custom Report', icon: FiIcons.FiSettings, desc: 'Build a custom report template' }
                ].map((reportType, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleGenerateReport(reportType.type)}
                    disabled={generatingReport}
                    className="p-4 border-2 border-slate-200 rounded-lg hover:border-grn-navy hover:bg-blue-50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-grn-navy/10 rounded-lg">
                        <SafeIcon icon={reportType.icon} className="text-grn-navy" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800 text-sm mb-1">{reportType.type}</h4>
                        <p className="text-xs text-slate-600">{reportType.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {generatingReport && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiIcons.FiLoader} className="text-blue-600 animate-spin text-xl" />
                    <div>
                      <p className="text-sm font-bold text-blue-900">Generating Report...</p>
                      <p className="text-xs text-blue-700">This may take a few moments. Please wait.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legal Framework Notice */}
      <Card className="bg-grn-navy text-white">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-xl shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold mb-2">Report Repository Legal Framework</p>
            <p className="opacity-90">
              All reports in this repository are generated in compliance with the <strong>Audit Act (2005)</strong>,{' '}
              <strong>Public Procurement Act (2015)</strong>, <strong>State Finance Act (1991)</strong>, and{' '}
              <strong>NDP6 IMEP Framework</strong>. Reports marked as "Confidential" or "Highly Confidential" are{' '}
              protected under the <strong>Official Secrets Act</strong> and unauthorized disclosure may result in{' '}
              criminal prosecution.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};